import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSequence } from '../context/SequenceContext';
import SafeIcon from '../common/SafeIcon';
import EmailTaskQueue from '../components/email/EmailTaskQueue';
import SequencePerformanceAnalytics from '../components/email/SequencePerformanceAnalytics';
import BaseModal from '../components/modals/BaseModal';

const EmailSequences = () => {
  const { sequences, addSequence } = useSequence();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tasks');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSeqName, setNewSeqName] = useState('');

  const handleCreate = (e) => {
    e.preventDefault();
    if (newSeqName) {
      const newSeq = addSequence({ name: newSeqName });
      setIsModalOpen(false);
      navigate(`/sequences/${newSeq.sequence_id}/edit`);
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto min-h-screen space-y-8">
      <div className="flex justify-between items-center border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">E-post Sekvenser</h1>
          <p className="text-slate-500 font-medium mt-1">Automatiser oppfølging med human-in-the-loop.</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg">
          <SafeIcon icon="➕" /> Ny Sekvens
        </button>
      </div>

      <div className="flex gap-1 bg-slate-100/50 p-1 rounded-xl w-fit">
        <button onClick={() => setActiveTab('tasks')} className={`px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'tasks' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          <SafeIcon icon="✅" className="mr-2" /> Oppgaver
        </button>
        <button onClick={() => setActiveTab('sequences')} className={`px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'sequences' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          <SafeIcon icon="🔄" className="mr-2" /> Sekvenser
        </button>
        <button onClick={() => setActiveTab('analytics')} className={`px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'analytics' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          <SafeIcon icon="📊" className="mr-2" /> Ytelse
        </button>
      </div>

      {activeTab === 'tasks' && <EmailTaskQueue />}

      {activeTab === 'analytics' && <SequencePerformanceAnalytics />}

      {activeTab === 'sequences' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sequences.map(seq => (
            <div key={seq.sequence_id} onClick={() => navigate(`/sequences/${seq.sequence_id}/edit`)} className="bg-white p-6 rounded-[2rem] border border-slate-200 hover:border-primary-300 shadow-sm hover:shadow-lg transition-all cursor-pointer group">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-slate-50 rounded-xl group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
                  <SafeIcon icon="🔄" className="w-6 h-6" />
                </div>
                <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-[10px] font-black uppercase tracking-widest border border-emerald-100">
                  {seq.status}
                </span>
              </div>
              <h3 className="text-lg font-black text-slate-900 mb-1">{seq.name}</h3>
              <p className="text-sm text-slate-500 font-medium">{seq.steps?.length || 0} steg</p>
              <div className="mt-6 pt-4 border-t border-slate-100 flex justify-between items-center text-xs text-slate-400 font-bold uppercase tracking-widest">
                <span>0 aktive</span>
                <span className="group-hover:translate-x-1 transition-transform">Rediger <SafeIcon icon="➡️" /></span>
              </div>
            </div>
          ))}
        </div>
      )}

      <BaseModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Opprett ny sekvens">
        <form onSubmit={handleCreate}>
          <label className="block text-sm font-bold mb-2">Navn på sekvens</label>
          <input autoFocus required value={newSeqName} onChange={e => setNewSeqName(e.target.value)} className="w-full border p-3 rounded-xl mb-4" placeholder="F.eks. Kaldt Salg - IT selskaper" />
          <div className="flex justify-end">
            <button type="submit" className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold">Opprett</button>
          </div>
        </form>
      </BaseModal>
    </div>
  );
};

export default EmailSequences;